<script setup lang="ts">
import type { PlaylistSummary } from '../types';

defineProps<{
  playlists: PlaylistSummary[];
}>();

const emit = defineEmits<{
  select: [id: string];
  playAll: [id: string];
}>();
</script>

<template>
  <div class="playlist-section">
    <div class="section-header">
      <h2 class="section-title">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 17a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9.5C2 8.12 3.12 7 4.5 7h.5"/>
          <path d="M16 2H8a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/>
          <path d="M12 11V7"/><path d="M9 9h6"/>
        </svg>
        播放列表
        <span class="count-badge">{{ playlists.length }}</span>
      </h2>
    </div>
    <div class="playlist-grid">
      <div
        v-for="pl in playlists"
        :key="pl.id"
        class="playlist-card"
        @click="emit('select', pl.id)"
      >
        <div class="card-accent"></div>
        <div class="card-content">
          <h3 class="pl-name">{{ pl.name }}</h3>
          <p v-if="pl.description" class="pl-desc">{{ pl.description }}</p>
          <div class="pl-meta">
            <span class="episode-count">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="2" width="20" height="20" rx="2.18"/>
                <path d="M7 2v20M17 2v20M2 12h20"/>
              </svg>
              {{ pl.episodeCount }} 集
            </span>
            <button class="play-all-btn" @click.stop="emit('playAll', pl.id)">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
              全部播放
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.playlist-section {
  margin-bottom: 32px;
  width: 100%;
}

.section-header {
  margin-bottom: 16px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary);
}

.count-badge {
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 12px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 100px;
}

.playlist-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
  width: 100%;
}

.playlist-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  cursor: pointer;
  overflow: hidden;
  display: flex;
  transition: all var(--transition-normal);
  animation: fadeInUp 0.4s ease-out both;
}
.playlist-card:hover {
  border-color: var(--accent);
  transform: translateY(-2px);
  box-shadow: var(--shadow-elevated), var(--shadow-glow);
}

.card-accent {
  width: 4px;
  flex-shrink: 0;
  background: var(--accent-gradient);
}

.card-content {
  flex: 1;
  padding: 16px 18px;
}

.pl-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 1.4;
}

.pl-desc {
  color: var(--text-secondary);
  font-size: 13px;
  margin-bottom: 12px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pl-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.episode-count {
  color: var(--text-muted);
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.play-all-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--accent-soft);
  color: var(--accent);
  border: 1px solid transparent;
  padding: 7px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all var(--transition-fast);
}
.play-all-btn:hover {
  background: var(--accent);
  color: white;
  border-color: var(--accent);
}

@media (max-width: 768px) {
  .playlist-section {
    padding: 0 16px;
    margin-bottom: 24px;
    max-width: 100%;
  }

  .playlist-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .card-content {
    padding: 14px;
  }

  .pl-name {
    font-size: 15px;
  }

  .play-all-btn {
    padding: 8px 12px;
    font-size: 12px;
  }
}
</style>
